import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assignmentAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiDownload, FiCheckCircle, FiClock, FiX } from 'react-icons/fi';
import { format } from 'date-fns';

const AssignmentSubmissions = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [gradeData, setGradeData] = useState({
    marks_obtained: '',
    feedback: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assignmentRes, submissionsRes] = await Promise.all([
        assignmentAPI.getAssignment(id),
        assignmentAPI.getSubmissions(id)
      ]);

      if (assignmentRes.data.success) {
        setAssignment(assignmentRes.data.data);
      }
      if (submissionsRes.data.success) {
        setSubmissions(submissionsRes.data.data);
      }
    } catch (error) {
      toast.error('Error loading data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGrade = (submission) => {
    setSelectedSubmission(submission);
    setGradeData({
      marks_obtained: submission.marks_obtained || '',
      feedback: submission.feedback || ''
    });
    setShowGradeModal(true);
  };

  const handleSubmitGrade = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await assignmentAPI.gradeSubmission(selectedSubmission.id, gradeData);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowGradeModal(false);
        setSelectedSubmission(null);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to grade submission');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      graded: 'bg-green-100 text-green-800',
      late: 'bg-red-100 text-red-800'
    };
    return badges[status] || badges.pending;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'graded':
        return <FiCheckCircle className="text-green-600" />;
      case 'late':
        return <FiX className="text-red-600" />;
      default:
        return <FiClock className="text-yellow-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/assignments')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft /> Back to Assignments
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          {assignment?.title} - Submissions
        </h1>
        <p className="text-sm md:text-base text-gray-600 mt-1">
          Total Submissions: {submissions.length}
        </p>
      </div>

      {/* Assignment Info Card */}
      {assignment && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Class</p>
              <p className="font-semibold text-gray-900">{assignment.class_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Due Date</p>
              <p className="font-semibold text-gray-900">
                {format(new Date(assignment.due_date), 'MMM dd, yyyy')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Marks</p>
              <p className="font-semibold text-gray-900">{assignment.total_marks}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className="font-semibold text-gray-900 capitalize">{assignment.status}</p>
            </div>
          </div>
        </div>
      )}

      {/* Submissions List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {submissions.length === 0 ? (
          <div className="text-center py-12">
            <FiClock size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Submissions Yet</h3>
            <p className="text-gray-600">Students haven't submitted their work yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attachment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {submissions.map(submission => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{submission.student_name}</div>
                      <div className="text-sm text-gray-500">{submission.student_email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {format(new Date(submission.submitted_at), 'MMM dd, yyyy HH:mm')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(submission.status)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(submission.status)}`}>
                          {submission.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {submission.marks_obtained !== null ? (
                        <span className="font-semibold text-gray-900">
                          {submission.marks_obtained} / {assignment.total_marks}
                        </span>
                      ) : (
                        <span className="text-gray-400">Not graded</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {submission.submission_file_path && (
                        <a
                          href={submission.submission_file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                        >
                          <FiDownload size={14} /> Download
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleGrade(submission)}
                        className="px-3 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 transition-colors"
                      >
                        {submission.status === 'graded' ? 'Edit Grade' : 'Grade'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Grade Modal */}
      {showGradeModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Grade Submission
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Student: <span className="font-semibold">{selectedSubmission.student_name}</span>
              </p>

              <form onSubmit={handleSubmitGrade} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marks Obtained * (Max: {assignment.total_marks})
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={assignment.total_marks}
                    step="0.01"
                    value={gradeData.marks_obtained}
                    onChange={(e) => setGradeData({ ...gradeData, marks_obtained: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Feedback (Optional)
                  </label>
                  <textarea
                    value={gradeData.feedback}
                    onChange={(e) => setGradeData({ ...gradeData, feedback: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                    rows="4"
                    placeholder="Provide feedback to the student..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-300"
                  >
                    {submitting ? 'Saving...' : 'Submit Grade'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowGradeModal(false);
                      setSelectedSubmission(null);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentSubmissions;
